/**
 * NOT-226 — shared helpers for labelling Time & Materials (rate-based) line
 * items on the customer-facing document (accept page + PDF) so a rate-based
 * line isn't mistaken for a fixed price. Used by both surfaces to stay
 * consistent.
 */

import { isMaterializedDynamicService } from './pricingMode';

interface TMLineLike {
  priceStrategy?: string | null;
  estimatedHours?: number | null;
  laborRate?: number | null;
}

/** A line billed on time & materials rather than a flat price. */
export function isTMLine(li: TMLineLike): boolean {
  return li.priceStrategy === 'DYNAMIC';
}

/** Whether any line in the set is time & materials. */
export function hasTMLines(items: TMLineLike[]): boolean {
  return items.some(isTMLine);
}

/**
 * Customer-facing basis line for a T&M item, e.g.
 * "Est. 3 hrs @ $150.00/hr + materials". Materials are billed on actuals, so
 * the basis appends "+ materials" as static copy.
 */
export function tmBasis(li: TMLineLike, fmt: (n: number) => string): string {
  const hrs = li.estimatedHours ?? undefined;
  const rate = li.laborRate ?? undefined;
  if (hrs && rate) {
    return `Est. ${hrs} hr${hrs === 1 ? '' : 's'} @ ${fmt(rate)}/hr + materials`;
  }
  if (rate) {
    return `Rate-based @ ${fmt(rate)}/hr — billed on actual time & materials`;
  }
  return 'Rate-based — billed on actual time & materials';
}

interface TMBilledLineLike {
  type?: string | null;
  priceStrategy?: string | null;
  quantity?: number | null;
  unitPrice?: number | null;
  estimatedHours?: number | null;
  actualHours?: number | null;
  laborRate?: number | null;
  /** Present (and enabled) on a MATERIALIZED dynamic service — see below. */
  dynamicPricingConfig?: { enabled?: boolean } | null;
}

function toNumber(value: unknown, fallback = 0) {
  const next = Number(value ?? fallback);
  return Number.isFinite(next) ? next : fallback;
}

/**
 * A line billed by the hour. Broader than {@link isTMLine}: it also catches
 * service lines that carry hours + a labor rate but no strategy at all.
 */
export function isTMBilledLine(li: TMBilledLineLike): boolean {
  // A MATERIALIZED dynamic service is not billed by the hour. The pricebook ran
  // the formula and the document CARRIES the one number it produced — it is FLAT
  // here (SPEC-line-item-model, "A materialized dynamic service is FLAT on the
  // document"). Reading it as hourly invents a basis it does not have: the row
  // printed "3 h x $216.00/hr" for a service quoted at $216, and in the invoice
  // editor it also brought an Hourly rate control that wrote straight through to
  // `unitPrice` — offering to rewrite a price that is immutable on the document
  // (rule 3). Asked FIRST, ahead of the strategy, because the strategy IS
  // `DYNAMIC` on exactly these lines.
  if (isMaterializedDynamicService(li)) return false;

  const strategy = String(li.priceStrategy ?? '')
    .trim()
    .toUpperCase();
  if (strategy === 'DYNAMIC') return true;

  // An explicit strategy is authoritative — never re-read a FLAT line as
  // hourly, and that matters more now than it did: a flat service records hours
  // deliberately, as COST, and printing "3 h @ $x/hr" against one would tell
  // the customer their fixed price was really an hourly bill. Hours also
  // survive a Dynamic -> Flat toggle (the update path merges the stored
  // `estimatedHours`/`actualHours` back when the client omits them) and
  // `laborRate` falls back to the unit price, so both would read as present.
  // Only fall through to the heuristic when there is no strategy to trust.
  if (strategy) return false;

  return (
    String(li.type ?? '').toUpperCase() === 'SERVICE' &&
    (li.actualHours != null || li.estimatedHours != null) &&
    toNumber(li.laborRate) > 0
  );
}

/**
 * Billed hours for the whole T&M LINE.
 *
 * `actualHours`/`estimatedHours` state the time ONE of the service takes, so the
 * line's hours are that time times the quantity — the same multiplication that
 * produced the amount printed beside them (NOT-630). A line SHOWS what it
 * CONTRIBUTES: printing one unit's hours against a doubled amount tells the
 * customer their two-hour job is being billed at twice the rate they agreed.
 *
 * Only where hours are actually STATED. With none, `quantity` is the hours — the
 * shape a line edited in the invoice editor's T&M control used to be saved in —
 * and multiplying it by itself would square them.
 */
export function tmLineHours(li: TMBilledLineLike): number {
  const stated = li.actualHours ?? li.estimatedHours;
  if (stated == null) return toNumber(li.quantity, 0);
  return toNumber(stated, 0) * toNumber(li.quantity, 1);
}

/**
 * Hourly rate for a T&M line. Job/estimate-derived lines collapse `unitPrice`
 * into the whole line subtotal (hours x rate), so `laborRate` wins when set.
 */
export function tmLineRate(li: TMBilledLineLike): number {
  return toNumber(li.laborRate ?? li.unitPrice, 0);
}

/**
 * Hours + rate to print in the Qty/Price columns of a customer-facing
 * document, or `null` when the line should render as plain quantity x price.
 *
 * NOT-226 — without this the customer sees "Qty 1 @ $20,000.00" for a line the
 * business quoted as "2 h @ $10,000.00/hr", because the unit price of a
 * job-derived T&M line is the collapsed line subtotal. Falls back to the plain
 * columns when the hours or the rate are missing, so a materials-only T&M line
 * never renders as "0 h @ $0.00/h".
 */
export function tmBilledDisplay(li: TMBilledLineLike): { hours: number; rate: number } | null {
  if (!isTMBilledLine(li)) return null;
  const hours = tmLineHours(li);
  const rate = tmLineRate(li);
  if (hours <= 0 || rate <= 0) return null;
  return { hours, rate };
}

/** Format billed hours without trailing zeros, e.g. 2 -> "2", 2.5 -> "2.5". */
export function formatHours(hours: number): string {
  return new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(hours);
}

/** Shared disclaimer copy shown when an estimate includes ≥1 T&M line. */
export const TM_APPROVAL_DISCLAIMER =
  'This estimate includes time & materials lines. The amounts shown for those ' +
  'lines are estimates — final billing is based on actual time and materials.';
